import React, { useMemo } from 'react';

import { css } from '@emotion/css';
import { Theme, Typography } from '@mui/material';
import { CellContext } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/assets/icons';
import { useStyles } from '@/hooks/useStyles.ts';
import { TelemonitoringTags } from '@/models/CarePlanModel.ts';
import {
  PatientListItem,
  telemonitoringTagColorMapping,
  telemonitoringTagTranslationMapping,
} from '@/models/PatientSummaryModel.ts';
import { Profession } from '@/models/ProfessionModel.ts';
import { userFormattedName } from '@/models/UserModel.ts';
import { PatientTableRenewButton } from '@/pages/patients/PatientTableRenewButton.tsx';
import { Tag } from '@/uiKit/atoms/Tag.tsx';

type BasePatientNameColumnProps = CellContext<PatientListItem, string>;
export type DoctorPatientNameColumnProps = BasePatientNameColumnProps;
export type NursePatientNameColumnProps = BasePatientNameColumnProps;
export type PatientNameColumnProps = BasePatientNameColumnProps & {
  qualification: Profession;
};

export const DoctorPatientNameColumn: React.FC<
  DoctorPatientNameColumnProps
> = props => <PatientNameColumn {...props} qualification="doctor" />;

export const NursePatientNameColumn: React.FC<
  NursePatientNameColumnProps
> = props => <PatientNameColumn {...props} qualification="nurse" />;

const PatientNameColumn: React.FC<PatientNameColumnProps> = ({
  row,
  hovered,
  qualification,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const { id, familyName, givenName, tags, renewalSuggestion } = row.original;
  // Show one of the following tags: 'expiring_soon', 'expired_recently', 'no_glycemia_data', 'nurse_asked_for_reinclusion', 'expired' (only if 'nurse_asked_for_reinclusion' is present)
  const tagsToDisplay: TelemonitoringTags[] = useMemo(() => {
    const tagsGenerated = () => {
      const tagsToAdd: TelemonitoringTags[] = [];
      if (tags.includes('nurse_asked_for_reinclusion')) {
        tagsToAdd.push('nurse_asked_for_reinclusion');
      }

      if (tags.includes('expiring_soon')) {
        tagsToAdd.push('expiring_soon');
        return tagsToAdd;
      }
      if (tags.includes('expired_recently')) {
        tagsToAdd.push('expired_recently');
        return tagsToAdd;
      }
      if (
        tags.includes('no_glycemia_data') &&
        (tags.includes('active') || tags.includes('gestational_no_insulin'))
      ) {
        tagsToAdd.push('no_glycemia_data');
        return tagsToAdd;
      }

      if (
        tags.includes('expired') &&
        tags.includes('nurse_asked_for_reinclusion')
      ) {
        tagsToAdd.push('expired');
        return tagsToAdd;
      }

      return tagsToAdd;
    };

    return tagsGenerated().sort((a, b) => {
      if (a === 'nurse_asked_for_reinclusion') return 1;
      if (b === 'nurse_asked_for_reinclusion') return -1;
      return 0;
    });
  }, [tags]);

  const tagElements = (
    <div className={styles.padding}>
      {tagsToDisplay.map(tag => (
        <Tag
          key={`${id}${tag}`}
          label={t(telemonitoringTagTranslationMapping(tag))}
          color={telemonitoringTagColorMapping(tag)}
          icon={
            tag === 'nurse_asked_for_reinclusion' ? (
              <Icons.stethoscope style={{ padding: 2 }} />
            ) : undefined
          }
        />
      ))}
    </div>
  );

  return (
    <div className={styles.container}>
      <Typography className={styles.name} variant="body">
        {userFormattedName(familyName, givenName)}
      </Typography>
      {qualification === 'doctor' ? (
        <PatientTableRenewButton
          id={`renew-tls-from-patient-list-${id}`}
          data-action="renew-tls"
          data-source="patient-list"
          data-patient={id}
          renewalSuggestion={renewalSuggestion}
          patientId={id}
          isButtonVisible={!!hovered && renewalSuggestion !== undefined}
        >
          {tagElements}
        </PatientTableRenewButton>
      ) : (
        tagElements
      )}
    </div>
  );
};

export const PatientNameHeader: React.FC = () => {
  return <div />;
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    padding-left: ${theme.spacing(8)};
    gap: ${theme.spacing(8)};
  `,
  name: css`
    font-weight: bold;
    text-wrap: wrap;
  `,
  padding: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `,
});
